import { getGoogleMapsApiKey } from './mapUtils';
import { loadGoogleMaps } from './googleMapsLoader';

export interface GoogleRouteResult {
  km: number;
  eta: string;
  durationSeconds: number;
  mapsUrl: string;
}

function formatEta(seconds: number) {
  if (!seconds || seconds < 0) return '';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes && days === 0) parts.push(`${minutes}m`);
  return parts.join(' ') || '0m';
}

function mapsDirUrl(points: string[]) {
  const origin = encodeURIComponent(points[0]);
  const destination = encodeURIComponent(points[points.length - 1]);
  const waypoints = points.slice(1, -1).map((p) => encodeURIComponent(p)).join('|');
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
  if (waypoints) url += `&waypoints=${waypoints}`;
  return url;
}

export async function computeDrivingRoute(points: string[]): Promise<GoogleRouteResult | null> {
  const stops = points.map((p) => p.trim()).filter(Boolean);
  if (stops.length < 2) return null;
  const key = getGoogleMapsApiKey();
  if (!key) return null;

  await loadGoogleMaps(key);
  const service = new google.maps.DirectionsService();

  const result = await new Promise<google.maps.DirectionsResult | null>((resolve) => {
    service.route(
      {
        origin: stops[0],
        destination: stops[stops.length - 1],
        waypoints: stops.slice(1, -1).map((location) => ({ location, stopover: true })),
        travelMode: google.maps.TravelMode.DRIVING,
        region: 'IN',
        optimizeWaypoints: false,
      },
      (res, status) => {
        if (status === 'OK' && res) resolve(res);
        else resolve(null);
      },
    );
  });

  const legs = result?.routes?.[0]?.legs || [];
  if (!legs.length) return null;

  const meters = legs.reduce((sum, leg) => sum + (leg.distance?.value || 0), 0);
  const seconds = legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0);
  const km = Math.round((meters / 1000) * 10) / 10;

  return {
    km,
    eta: formatEta(seconds),
    durationSeconds: seconds,
    mapsUrl: mapsDirUrl(stops),
  };
}

export function tripRoutePoints(form: Record<string, string>, stops: Array<{ location: string }>) {
  const type = form.tripType || 'one_way';
  const points = [form.source, ...stops.map((s) => s.location), form.destination];
  if (type === 'round_trip') {
    points.push(form.returnSource || form.destination, form.returnDestination || form.source);
  }
  return points.filter((p) => String(p || '').trim());
}
